// Landmark-marker -> canonical-joint inference for auto-rigging (docs/3d-
// content-pipeline/roadmap.md 3.3): a creator places a small number of
// markers on an uploaded T/A-pose mesh (the same landmark set Mixamo's own
// real, publicly documented workflow uses - chin, wrists, elbows, knees,
// groin), and this module turns those into rest-pose joint positions and
// skin-weight influence regions for Studio's own fixed seven-bone canonical
// topology (`CORE_RIG_BONES`, NeutralBiped.tsx) - not an arbitrary or
// variable skeleton, which is what makes this tractable where Mixamo's and
// Pinocchio's own version of the problem is hard.
//
// The geometry here is a deliberately simple, explainable heuristic, not a
// mesh-analysis algorithm (no cross-section scanning, no silhouette
// analysis): shoulders and hip sockets are inferred as a fixed fraction of
// the way from a torso landmark toward the matching limb's own elbow/knee
// marker, and feet are inferred by mirroring the thigh's own hip-to-knee
// vector below the knee (an equal-upper/lower-limb-length assumption). This
// is a reasonable first-pass proportion assumption for a standing reference
// pose, not a substitute for real anthropometric measurement - a visibly
// odd result on an unusual body shape is the honest cost of that
// simplicity, same spirit as `retarget.ts`'s own two documented
// limitations.
import { Vector3 } from 'three';
import { CORE_RIG_BONES, NEUTRAL_BIPED_RIG_ID } from '../core/NeutralBiped';
import type { BoneInfluenceSegment } from './autoRigGeometry';

export type Vector3Tuple = [number, number, number];

/** The Mixamo-style landmark set, in the creator's own uploaded mesh's
 * local space (T/A-pose). */
export interface LandmarkMarkers {
  chin: Vector3Tuple;
  leftWrist: Vector3Tuple;
  rightWrist: Vector3Tuple;
  leftElbow: Vector3Tuple;
  rightElbow: Vector3Tuple;
  leftKnee: Vector3Tuple;
  rightKnee: Vector3Tuple;
  groin: Vector3Tuple;
}

/** In marker placement order - the order the placement UI walks a creator
 * through. */
export const LANDMARK_ORDER: (keyof LandmarkMarkers)[] = [
  'groin', 'chin', 'leftElbow', 'leftWrist', 'rightElbow', 'rightWrist', 'leftKnee', 'rightKnee',
];

export const LANDMARK_LABELS: Record<keyof LandmarkMarkers, string> = {
  groin: 'Groin', chin: 'Chin', leftElbow: 'Left elbow', leftWrist: 'Left wrist',
  rightElbow: 'Right elbow', rightWrist: 'Right wrist', leftKnee: 'Left knee', rightKnee: 'Right knee',
};

/** The canonical rig's own bone order/names - auto-rig always targets these
 * seven exactly, so the result plugs into NeutralBiped's pose library and
 * every canonical clip as the identity mapping (3.3 item 4): no
 * bone-mapping step, because the bone names already match. */
export const AUTO_RIG_BONE_NAMES = CORE_RIG_BONES[NEUTRAL_BIPED_RIG_ID]!;

/** Parent-child hierarchy for the seven canonical bones, matching
 * NeutralBiped.tsx's own JSX nesting exactly (hips is the root; torso and
 * both legs hang off hips; the head and both arms hang off the torso). */
export const AUTO_RIG_HIERARCHY: Record<string, string | null> = {
  hips: null,
  torso: 'hips',
  head: 'torso',
  'left-arm': 'torso',
  'right-arm': 'torso',
  'left-leg': 'hips',
  'right-leg': 'hips',
};

/** How far from the torso landmark toward the elbow/knee the inferred
 * shoulder/hip socket sits, and how the mirrored-limb foot inference works
 * - see the module doc comment above. */
const SHOULDER_FRACTION = 0.45;
const HIP_SOCKET_FRACTION = 0.15;
const HEAD_LENGTH_FRACTION = 0.35;

/** Below this chin-groin separation (in the mesh's own local units), the
 * torso "up" direction is no longer well-defined - see the guard below. */
const MIN_MARKER_SEPARATION = 1e-6;

function v(t: Vector3Tuple): Vector3 {
  return new Vector3(t[0], t[1], t[2]);
}

export interface InferredJoint {
  name: string;
  /** World-space (mesh-local) rest-pose polyline this bone's influence
   * region follows, for skin-weight computation - a straight bone is 2
   * points, a bent limb (shoulder-elbow-wrist) is 3. */
  polyline: Vector3[];
  /** This bone's own rest-pose local position, relative to its parent bone
   * (or world space for the root, `hips`) - see the module doc comment for
   * why the pivot is always the polyline's own first point. */
  localPosition: Vector3Tuple;
}

/**
 * Infers all seven canonical joints/influence-segments from a landmark
 * marker set. Every bone's own rest LOCAL position is its polyline's first
 * point, expressed relative to its parent's own world pivot (which is, in
 * turn, always ITS OWN polyline's first point) - so `torso`'s pivot
 * coincides with `hips`'s (both start at `groin`), and `head`/`left-arm`/
 * `right-arm`'s local positions are each their own start point minus
 * `groin` (torso's world pivot). Every bone gets an identity rest rotation,
 * matching NeutralBiped's own rest convention (its JSX declares no
 * rotation on any bone group; poses only ever set rotation from that
 * baseline, see rendererRegistry.tsx's `applyPose`).
 */
export function inferCanonicalJoints(markers: LandmarkMarkers): InferredJoint[] {
  const groin = v(markers.groin);
  const chin = v(markers.chin);
  const leftElbow = v(markers.leftElbow);
  const rightElbow = v(markers.rightElbow);
  const leftWrist = v(markers.leftWrist);
  const rightWrist = v(markers.rightWrist);
  const leftKnee = v(markers.leftKnee);
  const rightKnee = v(markers.rightKnee);

  const torsoLength = groin.distanceTo(chin);
  // The `up` direction (chin - groin, normalised) is the one computation
  // here that is genuinely degenerate at zero separation - `Vector3.
  // normalize()` on a zero-length vector silently returns (0,0,0) rather
  // than NaN, which would collapse the head/torso axis instead of crashing,
  // so this has to be caught explicitly rather than relying on a NaN to
  // surface it. Every other joint below is a `lerp`/vector difference,
  // which stays well-defined (if visually degenerate) for any marker
  // placement, so this is the only guard actually needed.
  if (torsoLength < MIN_MARKER_SEPARATION) {
    throw new Error('The chin and groin markers are too close together - place them at opposite ends of the torso.');
  }
  const up = chin.clone().sub(groin).normalize();

  const shoulderL = chin.clone().lerp(leftElbow, SHOULDER_FRACTION);
  const shoulderR = chin.clone().lerp(rightElbow, SHOULDER_FRACTION);
  const hipSocketL = groin.clone().lerp(leftKnee, HIP_SOCKET_FRACTION);
  const hipSocketR = groin.clone().lerp(rightKnee, HIP_SOCKET_FRACTION);
  // Mirror the thigh (hip socket -> knee) below the knee for the foot -
  // an equal-upper/lower-leg-length assumption for a standing reference pose.
  const footL = leftKnee.clone().add(leftKnee.clone().sub(hipSocketL));
  const footR = rightKnee.clone().add(rightKnee.clone().sub(hipSocketR));
  const headTip = chin.clone().addScaledVector(up, torsoLength * HEAD_LENGTH_FRACTION);

  const localOf = (worldPivot: Vector3, parentWorldPivot: Vector3): Vector3Tuple => {
    const local = worldPivot.clone().sub(parentWorldPivot);
    return [local.x, local.y, local.z];
  };

  return [
    { name: 'hips', polyline: [groin], localPosition: [groin.x, groin.y, groin.z] },
    { name: 'torso', polyline: [groin, chin], localPosition: localOf(groin, groin) },
    { name: 'head', polyline: [chin, headTip], localPosition: localOf(chin, groin) },
    { name: 'left-arm', polyline: [shoulderL, leftElbow, leftWrist], localPosition: localOf(shoulderL, groin) },
    { name: 'right-arm', polyline: [shoulderR, rightElbow, rightWrist], localPosition: localOf(shoulderR, groin) },
    { name: 'left-leg', polyline: [hipSocketL, leftKnee, footL], localPosition: localOf(hipSocketL, groin) },
    { name: 'right-leg', polyline: [hipSocketR, rightKnee, footR], localPosition: localOf(hipSocketR, groin) },
  ];
}

/** The subset of `InferredJoint` `computeGeodesicSkinWeights` actually
 * needs, in the same order as `AUTO_RIG_BONE_NAMES` (so a weight's
 * `boneIndex` lines up with a skeleton built in that same order). */
export function toInfluenceSegments(joints: InferredJoint[]): BoneInfluenceSegment[] {
  return joints.map((j) => ({ name: j.name, polyline: j.polyline }));
}
